<template>
  <div class="jsonEditorContainer">
    <div class="labelEditor">{{ title }}:</div>
    <JsonEditorVue
      :model-value="displayValue"
      @update:model-value="handleEditorChange"
      v-bind="{
        mode: modeRef,
        mainMenuBar: false,
        statusBar: false,
        navigationBar: false
      }"
    />
    <div class="statusBar">
      <button class="btn btn-default" v-auto-blur @click.prevent="save">Save</button>
      <button class="btn btn-default" v-auto-blur @click.prevent="initialize">Initialize</button>
      <button class="btn btn-warning" v-auto-blur @click.prevent="clear">Empty</button>
      <p v-if="unsavedChanges">Changes not yet saved!</p>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import JsonEditorVue from 'json-editor-vue';
import axios from 'axios';
import _ from 'lodash';

export default defineComponent({
  name: 'JsonEditorComponent',
  components: {JsonEditorVue},
  props: {
    title: {
      type: String,
      default: 'JSON Editor'
    },
    initialContent: {
      type: [Object, String],
      required: true
    },
    initialStructure: {
      type: Object,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    },
    csrfToken: {
      type: String,
      required: true
    },
    resourceType: {
      type: String,
      required: true
    }
  },
  setup(props) {
    let parsedContent;
    try {
      parsedContent = JSON.parse(props.initialContent);
    } catch (error) {
      console.error('Invalid JSON in initialContent: ', error);
      parsedContent = {};
    }

    const originalContent = ref(_.cloneDeep(parsedContent));
    const editorContentRef = ref(_.cloneDeep(parsedContent));
    const modeRef = ref('text');

    const unsavedChanges = computed(() => {
      return !_.isEqual(editorContentRef.value, originalContent.value);
    });

    const postData = async (content) => {
      try {
        await axios.post(props.endpoint, content, {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-CSRF-TOKEN': props.csrfToken,
            'API-Resource-Type': props.resourceType
          }
        }).then(() => {
          originalContent.value = _.cloneDeep(content);
          editorContentRef.value = _.cloneDeep(content);
        }).catch((error) => {
          const errorMessage = error.response?.data || 'An unexpected error occurred while saving';
          alert(errorMessage);
        });
      } catch (error) {
        console.error("Error has occurred while saving data:", error);
      }
    };

    function isValidJSON(value) {
      if (value === null) return true;

      // Check for undefined or empty strings
      if (value === undefined || (typeof value === 'string' && value.trim() === '')) return false;

      try {
        if (typeof value === 'string') {
          JSON.parse(value);
        } else {
          JSON.stringify(value);
        }
        return true;
      } catch (e) {
        return false;
      }
    }

    const save = () => {
      if (isValidJSON(editorContentRef.value) && unsavedChanges.value) {
        postData(editorContentRef.value);
      }
    };

    const initialize = () => {
      editorContentRef.value = _.cloneDeep(props.initialStructure);
    };

    const clear = () => {
      editorContentRef.value = null;
    };

    const displayValue = computed(() => {
      return editorContentRef.value === null ? '' : editorContentRef.value
    })

    const handleEditorChange = (newValue) => {
      editorContentRef.value = newValue === '' ? null : newValue;
    };

    return {
      editorContentRef,
      modeRef,
      unsavedChanges,
      save,
      initialize,
      clear,
      displayValue,
      handleEditorChange
    };
  }
});
</script>

<style scoped lang="scss">
div.jsonEditorContainer {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  div.labelEditor {
    margin-top: 10px;
    margin-bottom: 2px;
    font-weight: bold;
  }

  div.statusBar {
    display: flex;
    flex-direction: row;
    gap: 5px;
    margin-top: 2px;
    margin-bottom: 10px;

    p {
      color: indianred;
    }
  }
}

div.jse-main {
  max-height: 500px !important;
  height: fit-content;
}
</style>
